import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { Card } from '@/shared/ui/Card';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Select } from '@/shared/ui/Select';
import { useToast } from '@/shared/ui/Toast';
import { EditPracticeModal } from '../EditPracticeModal';
import {
  agencyApi,
  type Location,
  type Invitation,
  type PipelineStatus,
  type PracticeDetail,
} from '../../api/agencyApi';
import { agencyKeys } from '../../queryKeys';
import { formatDate } from '@/shared/utils/format';
import { practiceTypeLabel } from '@/shared/constants/verticals';
import { formatPhoneDisplay, validatePhone, PHONE_PLACEHOLDER } from '@/shared/utils/validation';
import styles from './PracticeSettingsCards.module.css';

const PRIMARY_PHONE_HINT =
  "Primary location: this is also the practice's phone on the Profile tab.";

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'neutral'> = {
  active: 'success',
  trial: 'warning',
  invited: 'warning',
  onboarding: 'warning',
  inactive: 'neutral',
  draft: 'neutral',
  suspended: 'neutral',
};

type LocationForm = {
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  website: string;
};
const EMPTY_LOC: LocationForm = {
  name: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
  website: '',
};

const PIPELINE_STAGES: Array<keyof PipelineStatus> = [
  'crawl',
  'extraction',
  'audit',
  'competitors',
];

function getPipelineBadge(
  status?: PipelineStatus | null,
): { label: string; variant: 'success' | 'warning' | 'neutral' | 'error' } | null {
  if (!status) return null;
  const stages = PIPELINE_STAGES.map((s) => status[s]);
  if (stages.every((s) => s === 'completed')) return { label: 'Ready', variant: 'success' };
  if (stages.some((s) => s === 'failed')) return { label: 'Setup error', variant: 'error' };
  if (stages.some((s) => s === 'in_progress' || s === 'pending' || s == null))
    return { label: 'Setting up…', variant: 'warning' };
  return null;
}

function LocationRow({
  loc,
  practiceId,
  onDone,
}: {
  loc: Location;
  practiceId: string;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);

  const badge = getPipelineBadge(loc.pipelineStatus);
  const isSettingUp = badge?.label === 'Setting up…';

  // Poll while pipeline is in progress
  useQuery({
    queryKey: agencyKeys.practice(practiceId),
    queryFn: () => agencyApi.getPractice(practiceId),
    refetchInterval: isSettingUp ? 5000 : false,
    enabled: isSettingUp,
  });

  const [form, setForm] = useState<LocationForm>({
    name: loc.name,
    address: loc.address ?? '',
    city: loc.city ?? '',
    state: loc.state ?? '',
    zip: loc.zip ?? '',
    phone: loc.phone ?? '',
    website: loc.website ?? '',
  });
  const [phoneError, setPhoneError] = useState('');

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: agencyKeys.practice(practiceId) });

  const updateMutation = useMutation({
    mutationFn: () =>
      agencyApi.updateLocation(practiceId, loc.id, { ...form, phone: form.phone || undefined }),
    onSuccess: () => {
      invalidate();
      toast.success('Location updated.');
      setEditing(false);
    },
    onError: () => toast.error('Failed to update location.'),
  });

  // The primary location's phone is the practice's phone, so switching primaries changes the
  // number on the Profile tab too.
  const makePrimaryMutation = useMutation({
    mutationFn: () => agencyApi.updateLocation(practiceId, loc.id, { isDefault: true }),
    onSuccess: () => {
      invalidate();
      toast.success(`${loc.name} is now the primary location.`);
    },
    onError: () => toast.error('Failed to change the primary location.'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => agencyApi.deleteLocation(practiceId, loc.id),
    onSuccess: () => {
      invalidate();
      toast.success('Location removed.');
      onDone();
    },
    onError: () => toast.error('Failed to delete location.'),
  });

  const handleSave = () => {
    const err = validatePhone(form.phone);
    if (err) {
      setPhoneError(err);
      return;
    }
    updateMutation.mutate();
  };

  const addressLine = [loc.address, loc.city, loc.state, loc.zip].filter(Boolean).join(', ');

  if (editing) {
    return (
      <div className={styles.locForm}>
        <Input
          label="Name"
          value={form.name}
          onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          fullWidth
        />
        <Input
          label="Phone"
          type="tel"
          value={form.phone}
          onChange={(e) => {
            setForm((p) => ({ ...p, phone: e.target.value }));
            setPhoneError('');
          }}
          onBlur={(e) => setForm((p) => ({ ...p, phone: formatPhoneDisplay(e.target.value) }))}
          placeholder={PHONE_PLACEHOLDER}
          error={phoneError}
          hint={loc.isDefault ? PRIMARY_PHONE_HINT : undefined}
          fullWidth
        />
        <Input
          label="Website"
          value={form.website}
          onChange={(e) => setForm((p) => ({ ...p, website: e.target.value }))}
          placeholder="https://example.com"
          fullWidth
        />
        <Input
          label="Address"
          value={form.address}
          onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
          fullWidth
        />
        <div className={styles.locFormGrid}>
          <Input
            label="City"
            value={form.city}
            onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
            fullWidth
          />
          <Input
            label="State"
            value={form.state}
            onChange={(e) => setForm((p) => ({ ...p, state: e.target.value }))}
            fullWidth
          />
          <Input
            label="ZIP"
            value={form.zip}
            onChange={(e) => setForm((p) => ({ ...p, zip: e.target.value }))}
            fullWidth
          />
        </div>
        <div className={styles.locFormActions}>
          <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
            Cancel
          </Button>
          <Button size="sm" loading={updateMutation.isPending} onClick={handleSave}>
            Save
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.locRow}>
      <div className={styles.locMain}>
        <div className={styles.locHeader}>
          <span className={styles.locName}>{loc.name}</span>
          {loc.isDefault && (
            <Badge variant="success" size="sm">
              Primary
            </Badge>
          )}
          {badge && (
            <Badge variant={badge.variant} size="sm">
              {badge.label}
            </Badge>
          )}
        </div>
        {addressLine && <div className={styles.locAddress}>{addressLine}</div>}
        {loc.phone && (
          <div className={styles.locPhone}>
            {loc.phone}
            {loc.isDefault && <span className={styles.locPhoneNote}> · practice phone</span>}
          </div>
        )}
      </div>
      <div className={styles.locActions}>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate(`/agency/practices/${practiceId}/locations/${loc.id}/onboarding`)}
        >
          {badge?.label === 'Ready' ? 'Re-onboard' : 'Onboard'}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
          Edit
        </Button>
        {!loc.isDefault && (
          <Button
            variant="ghost"
            size="sm"
            loading={makePrimaryMutation.isPending}
            onClick={() => makePrimaryMutation.mutate()}
          >
            Make primary
          </Button>
        )}
        {!loc.isDefault && (
          <Button
            variant="ghost"
            size="sm"
            loading={deleteMutation.isPending}
            onClick={() => deleteMutation.mutate()}
          >
            Delete
          </Button>
        )}
      </div>
    </div>
  );
}

function AddLocationForm({ practiceId, onDone }: { practiceId: string; onDone: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<LocationForm>(EMPTY_LOC);
  const [phoneError, setPhoneError] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      agencyApi.addLocation(practiceId, {
        ...form,
        phone: form.phone || undefined,
        isDefault: false,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agencyKeys.practice(practiceId) });
      toast.success('Location added.');
      onDone();
    },
    onError: () => toast.error('Failed to add location.'),
  });

  const handleSave = () => {
    const err = validatePhone(form.phone);
    if (err) {
      setPhoneError(err);
      return;
    }
    mutation.mutate();
  };

  return (
    <div className={styles.locFormAdd}>
      <h5 className={styles.locFormTitle}>New Location</h5>
      <Input
        label="Name"
        value={form.name}
        onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
        fullWidth
      />
      <Input
        label="Phone"
        type="tel"
        value={form.phone}
        onChange={(e) => {
          setForm((p) => ({ ...p, phone: e.target.value }));
          setPhoneError('');
        }}
        onBlur={(e) => setForm((p) => ({ ...p, phone: formatPhoneDisplay(e.target.value) }))}
        placeholder={PHONE_PLACEHOLDER}
        error={phoneError}
        fullWidth
      />
      <Input
        label="Website"
        value={form.website}
        onChange={(e) => setForm((p) => ({ ...p, website: e.target.value }))}
        placeholder="https://example.com"
        fullWidth
      />
      <Input
        label="Address"
        value={form.address}
        onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
        fullWidth
      />
      <div className={styles.locFormGrid}>
        <Input
          label="City"
          value={form.city}
          onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
          fullWidth
        />
        <Input
          label="State"
          value={form.state}
          onChange={(e) => setForm((p) => ({ ...p, state: e.target.value }))}
          fullWidth
        />
        <Input
          label="ZIP"
          value={form.zip}
          onChange={(e) => setForm((p) => ({ ...p, zip: e.target.value }))}
          fullWidth
        />
      </div>
      <div className={styles.locFormActions}>
        <Button variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
        <Button
          size="sm"
          loading={mutation.isPending}
          onClick={handleSave}
          disabled={!form.name.trim()}
        >
          Add Location
        </Button>
      </div>
    </div>
  );
}

export function InvitationsCard({ practice }: { practice: PracticeDetail }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ email: '', contactName: '', role: 'owner' });
  const [errors, setErrors] = useState<{ email?: string; contactName?: string }>({});
  const [conflict, setConflict] = useState<{
    existingPractices: Array<{ id: string; name: string }>;
  } | null>(null);

  const doSend = (force?: boolean) =>
    agencyApi.sendInvitation(practice.id, form.email, form.contactName, form.role, force);

  const sendMutation = useMutation({
    mutationFn: () => doSend(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agencyKeys.practice(practice.id) });
      queryClient.invalidateQueries({ queryKey: agencyKeys.practices() });
      toast.success('Invitation sent.');
      setSending(false);
      setForm({ email: '', contactName: '', role: 'owner' });
    },
    onError: (err: unknown) => {
      const apiErr = err as {
        statusCode?: number;
        message?: string;
        data?: {
          requiresConfirmation?: boolean;
          existingPractices?: Array<{ id: string; name: string }>;
        };
      };
      if (apiErr.statusCode === 409 && apiErr.data?.requiresConfirmation) {
        setConflict({ existingPractices: apiErr.data.existingPractices ?? [] });
      } else {
        toast.error(apiErr.message || 'Failed to send invitation.');
      }
    },
  });

  const forceSendMutation = useMutation({
    mutationFn: () => doSend(true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agencyKeys.practice(practice.id) });
      queryClient.invalidateQueries({ queryKey: agencyKeys.practices() });
      toast.success('Invitation sent.');
      setSending(false);
      setConflict(null);
      setForm({ email: '', contactName: '', role: 'owner' });
    },
    onError: () => toast.error('Failed to send invitation.'),
  });

  const handleSend = () => {
    const newErrors: typeof errors = {};
    if (!form.contactName.trim()) newErrors.contactName = 'Name is required';
    if (!form.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Invalid email';
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) sendMutation.mutate();
  };

  return (
    <Card title="Invitations">
      <div className={styles.invList}>
        {(practice.invitations ?? []).length === 0 && !sending && (
          <p className={styles.invEmpty}>No invitations sent.</p>
        )}
        {(practice.invitations ?? []).map((inv) => (
          <InvitationRow key={inv.id} inv={inv} practiceId={practice.id} onRevoked={() => {}} />
        ))}
      </div>

      {conflict && (
        <div className={styles.invConflict}>
          <div className={styles.invConflictTitle}>User already belongs to another practice</div>
          <div className={styles.invConflictBody}>
            <strong>{form.email}</strong> is already a member of {conflict.existingPractices.length}{' '}
            other {conflict.existingPractices.length === 1 ? 'practice' : 'practices'} &mdash;{' '}
            {conflict.existingPractices.map((p, i, arr) => (
              <span key={p.id}>
                {i > 0 && (i === arr.length - 1 ? ' and ' : ', ')}
                {p.name}
              </span>
            ))}
            . Sending this invitation will allow them to join this practice as well.
          </div>
          <div className={styles.locFormActions}>
            <Button variant="ghost" size="sm" onClick={() => setConflict(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              loading={forceSendMutation.isPending}
              onClick={() => forceSendMutation.mutate()}
            >
              Send Anyway
            </Button>
          </div>
        </div>
      )}

      {sending && !conflict ? (
        <div className={styles.invForm}>
          <Input
            label="Contact Name"
            value={form.contactName}
            onChange={(e) => {
              setForm((p) => ({ ...p, contactName: e.target.value }));
              setErrors((p) => ({ ...p, contactName: '' }));
            }}
            error={errors.contactName}
            placeholder="Dr. Jane Smith"
            fullWidth
          />
          <Input
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => {
              setForm((p) => ({ ...p, email: e.target.value }));
              setErrors((p) => ({ ...p, email: '' }));
            }}
            error={errors.email}
            placeholder="contact@practice.com"
            fullWidth
          />
          <Select
            label="Role"
            value={form.role}
            onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))}
            options={[
              { value: 'owner', label: 'Owner' },
              { value: 'admin', label: 'Admin' },
              { value: 'manager', label: 'Manager' },
              { value: 'viewer', label: 'Viewer' },
            ]}
          />
          <div className={styles.locFormActions}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSending(false);
                setForm({ email: '', contactName: '', role: 'owner' });
                setErrors({});
              }}
            >
              Cancel
            </Button>
            <Button size="sm" loading={sendMutation.isPending} onClick={handleSend}>
              Send Invitation
            </Button>
          </div>
        </div>
      ) : (
        !conflict && (
          <div
            className={
              (practice.invitations ?? []).length > 0 ? styles.invSendBordered : styles.invSend
            }
          >
            <Button variant="secondary" size="sm" onClick={() => setSending(true)}>
              + Send Invitation
            </Button>
          </div>
        )
      )}
    </Card>
  );
}

const INVITE_STATUS_VARIANT: Record<string, 'success' | 'warning' | 'neutral'> = {
  pending: 'warning',
  accepted: 'success',
  expired: 'neutral',
  revoked: 'neutral',
};

function InvitationRow({
  inv,
  practiceId,
  onRevoked,
}: {
  inv: Invitation;
  practiceId: string;
  onRevoked: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [resendConflict, setResendConflict] = useState<{
    existingPractices: Array<{ id: string; name: string }>;
  } | null>(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: agencyKeys.practice(practiceId) });
    queryClient.invalidateQueries({ queryKey: agencyKeys.practices() });
  };

  const revokeMutation = useMutation({
    mutationFn: () => agencyApi.revokeInvitation(practiceId, inv.id),
    onSuccess: () => {
      invalidate();
      toast.success('Invitation revoked.');
      onRevoked();
    },
    onError: () => toast.error('Failed to revoke invitation.'),
  });

  const resendMutation = useMutation({
    mutationFn: (force: boolean | undefined) =>
      agencyApi.resendInvitation(practiceId, inv.id, force),
    onSuccess: () => {
      invalidate();
      toast.success('Invitation resent.');
      setResendConflict(null);
    },
    onError: (err: unknown) => {
      const apiErr = err as {
        statusCode?: number;
        data?: {
          requiresConfirmation?: boolean;
          existingPractices?: Array<{ id: string; name: string }>;
        };
      };
      if (apiErr.statusCode === 409 && apiErr.data?.requiresConfirmation) {
        setResendConflict({ existingPractices: apiErr.data.existingPractices ?? [] });
      } else {
        toast.error('Failed to resend invitation.');
      }
    },
  });

  const extendMutation = useMutation({
    mutationFn: () => agencyApi.extendInvitation(practiceId, inv.id),
    onSuccess: () => {
      invalidate();
      toast.success('Invitation extended by 15 days.');
    },
    onError: (err: unknown) => {
      const apiErr = err as { message?: string };
      toast.error(apiErr.message || 'Failed to extend invitation.');
    },
  });

  const canResend = inv.status !== 'accepted';
  // Extending only makes sense while the owner still has a live link to use.
  const canExtend = inv.status === 'pending';

  return (
    <>
      <div className={styles.invRow}>
        <div>
          <div className={styles.invEmail}>{inv.email}</div>
          <div className={styles.invMeta}>
            Sent {formatDate(inv.sentAt)} · Expires {formatDate(inv.expiresAt)}
          </div>
        </div>
        <div className={styles.invActions}>
          <Badge variant={INVITE_STATUS_VARIANT[inv.status] ?? 'neutral'} size="sm">
            {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
          </Badge>
          {canResend && (
            <Button
              variant="ghost"
              size="sm"
              loading={resendMutation.isPending && !resendConflict}
              onClick={() => resendMutation.mutate(undefined)}
            >
              Resend
            </Button>
          )}
          {canExtend && (
            <Button
              variant="ghost"
              size="sm"
              loading={extendMutation.isPending}
              onClick={() => extendMutation.mutate()}
              title="Push the expiry out by 15 days without sending a new link"
            >
              Extend
            </Button>
          )}
          {canExtend && (
            <Button
              variant="ghost"
              size="sm"
              loading={revokeMutation.isPending}
              onClick={() => revokeMutation.mutate()}
            >
              Revoke
            </Button>
          )}
        </div>
      </div>
      {resendConflict && (
        <div className={styles.resendConflict}>
          <div className={styles.resendConflictBody}>
            <strong>{inv.email}</strong> is already a member of{' '}
            {resendConflict.existingPractices.map((p, i, arr) => (
              <span key={p.id}>
                {i > 0 && (i === arr.length - 1 ? ' and ' : ', ')}
                {p.name}
              </span>
            ))}
            .
          </div>
          <div className={styles.locFormActions}>
            <Button variant="ghost" size="sm" onClick={() => setResendConflict(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              loading={resendMutation.isPending}
              onClick={() => resendMutation.mutate(true)}
            >
              Resend Anyway
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export function TeamMembersCard({ practice }: { practice: PracticeDetail }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [removingId, setRemovingId] = useState<string | null>(null);

  const removeMutation = useMutation({
    mutationFn: (userId: string) => agencyApi.removeTeamMember(practice.id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agencyKeys.practice(practice.id) });
      queryClient.invalidateQueries({ queryKey: agencyKeys.practices() });
      toast.success('Team member removed.');
      setRemovingId(null);
    },
    onError: () => {
      toast.error('Failed to remove team member.');
      setRemovingId(null);
    },
  });

  return (
    <Card title="Team Members">
      {(practice.users ?? []).length === 0 ? (
        <p className={styles.teamEmpty}>No users yet.</p>
      ) : (
        <div className={styles.teamList}>
          {(practice.users ?? []).map(({ user }) => (
            <div key={user.id} className={styles.teamRow}>
              <div className={styles.teamLeft}>
                <div className={styles.teamAvatar}>{user.firstName.charAt(0).toUpperCase()}</div>
                <div>
                  <div className={styles.teamName}>
                    {[user.firstName, user.lastName].filter(Boolean).join(' ')}
                  </div>
                  <div className={styles.teamEmail}>{user.email}</div>
                  <div className={styles.teamLastLogin}>
                    {user.lastLoginAt
                      ? `Last login ${new Date(user.lastLoginAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                      : 'Never logged in'}
                  </div>
                </div>
              </div>
              {removingId === user.id ? (
                <div className={styles.teamConfirmRow}>
                  <Button variant="ghost" size="sm" onClick={() => setRemovingId(null)}>
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    loading={removeMutation.isPending}
                    onClick={() => removeMutation.mutate(user.id)}
                  >
                    Confirm
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setRemovingId(user.id)}
                  className={styles.teamRemoveBtn}
                >
                  Remove
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/**
 * The practice record: who they are. The Edit dialog and the internal/client
 * switch live here so the whole profile is one card.
 */
export function PracticeInfoCard({ practice }: { practice: PracticeDetail }) {
  const [editOpen, setEditOpen] = useState(false);
  const primaryLocation = (practice.locations ?? []).find((l) => l.isDefault);

  const creatorName = practice.createdByUser
    ? [practice.createdByUser.firstName, practice.createdByUser.lastName]
        .filter(Boolean)
        .join(' ') || practice.createdByUser.email
    : '—';

  return (
    <>
      <Card
        title="Practice Information"
        action={
          <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)}>
            Edit
          </Button>
        }
      >
        <dl className={styles.dl}>
          <div className={styles.row}>
            <dt>Status</dt>
            <dd>
              <Badge variant={STATUS_VARIANT[practice.status] ?? 'neutral'} size="sm">
                {practice.status.charAt(0).toUpperCase() + practice.status.slice(1)}
              </Badge>
            </dd>
          </div>
          {/*
            Always rendered, never conditional on specialty being set: the vertical
            drives competitor discovery, market benchmarks and generated copy, so an
            agency user needs to be able to confirm it on the practice record rather
            than infer it from the Edit dialog.
          */}
          <div className={styles.row}>
            <dt>Practice Type</dt>
            <dd>{practiceTypeLabel(practice.vertical, practice.specialty)}</dd>
          </div>
          <div className={styles.row}>
            <dt>Phone</dt>
            <dd>
              {practice.phone || '—'}
              {primaryLocation && (
                <div className={styles.fieldNote}>
                  {(practice.locations ?? []).length > 1
                    ? `From the primary location, ${primaryLocation.name}. Each location keeps its own number under Locations.`
                    : 'Same number as the location under Locations — editing either updates both.'}
                </div>
              )}
            </dd>
          </div>
          {practice.website && (
            <div className={styles.row}>
              <dt>Website</dt>
              <dd>
                <a href={practice.website} target="_blank" rel="noopener noreferrer">
                  {practice.website}
                </a>
              </dd>
            </div>
          )}
          <div className={styles.row}>
            <dt>Created</dt>
            <dd>{formatDate(practice.createdAt)}</dd>
          </div>
          <div className={styles.row}>
            <dt>Added By</dt>
            <dd>{creatorName}</dd>
          </div>
          <div className={styles.row}>
            <dt>Onboarding</dt>
            <dd>
              <Badge variant={practice.onboardingComplete ? 'success' : 'warning'} size="sm">
                {practice.onboardingComplete ? 'Complete' : 'Pending'}
              </Badge>
            </dd>
          </div>
        </dl>
      </Card>
      {editOpen && (
        <EditPracticeModal
          practice={practice}
          isOpen={editOpen}
          onClose={() => setEditOpen(false)}
        />
      )}
    </>
  );
}

/** Every location, with onboarding, edit and delete. Module and model settings live on their own tabs. */
export function LocationsCard({ practice }: { practice: PracticeDetail }) {
  const [addingLocation, setAddingLocation] = useState(false);

  return (
    <Card title={`Locations (${(practice.locations ?? []).length})`}>
      <div className={styles.locListInner}>
        {(practice.locations ?? []).map((loc) => (
          <LocationRow key={loc.id} loc={loc} practiceId={practice.id} onDone={() => {}} />
        ))}
        {(practice.locations ?? []).length === 0 && (
          <p className={styles.locListEmpty}>No locations yet.</p>
        )}
        {addingLocation ? (
          <AddLocationForm practiceId={practice.id} onDone={() => setAddingLocation(false)} />
        ) : (
          <div className={styles.locAddBtnRow}>
            <Button variant="secondary" size="sm" onClick={() => setAddingLocation(true)}>
              + Add Location
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
